import React, { useState, useEffect } from "react";
import DataTable from "../../Common/DataTable/DataTable";
import { getFollowUpList, addFollowupHistory} from "../../api";
import CustomSpinner from "../../Common/Spinner";
import { formatDateAndTime } from "../../utils/utils";
import styles from "./followup.module.css";
import { Offcanvas, Accordion } from "react-bootstrap";
import { followUpColumns } from "../../utils/tableHeader";
import { Button } from "../../Common";
import { AddFollowup } from "./AddFollowup";
import CommentModal from "../../Common/CommentModal/commentModal";
import { CookieUtils } from "../../axios.config";

const FollowUp = ({ custId, featureAccess }) => {
  const [dataLoading, setDataLoading] = useState(false);
  const [followUpData, setFollowUpData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showCanvas, setShowCanvas] = useState(false);
  const [canvasData, setCanvasData] = useState(null);
  const [activeKey, setActiveKey] = useState("0");
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedFollowupData, setSelectedFollowupData] = useState(null);

  let createdByUser = "";
  const cookieUserName = CookieUtils.get("username");
  if (cookieUserName) {
    try {
      createdByUser = JSON.parse(cookieUserName);
    } catch {
      createdByUser = cookieUserName;
    }
  }
  const userName = createdByUser;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isComment, setIsComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [currentAction, setCurrentAction] = useState("");

  const fetchFollowUpData = async (custId) => {
    try {
      setDataLoading(true);
      const response = await getFollowUpList(custId);

      if (response?.code === 200) {
        const followUpList = response?.data || [];
        if (Array.isArray(followUpList)) {
          setFollowUpData(followUpList);
          setErrorMessage("");
        } else {
          setErrorMessage("Invalid data format received");
        }
      } else {
        setErrorMessage(
          response?.data?.message || "Failed to fetch follow-up details"
        );
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Something went wrong");
    } finally {
      setDataLoading(false);
    }
  };

  const handleToggle = (key) => {
    setActiveKey((prev) => (prev === key ? null : key));
  };

  const handleClickRow = async (data) => {
    setCanvasData(null);
    setShowCanvas(true);
    setCanvasData(data);
  };

  const handleShowForm = () => {
    setEditMode(false);
    setSelectedFollowupData(null);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditMode(false);
    setSelectedFollowupData(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditMode(false);
    setSelectedFollowupData(null);
    fetchFollowUpData(custId);
  };
  const handleEditFollowupClick = (data) => {
    if (data.status === "closed") {
      setErrorMessage("Cannot edit follow-ups with 'closed' status");
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
      return;
    }
    setEditMode(true);
    setSelectedFollowupData(data);
    setShowForm(true);
    setShowCanvas(false);
  };

  const handleAddHistory = (data) => {
    setSelectedFollowupData(data);
    setIsModalOpen(true);
    setSuccessMessage("");
    setErrorMessage("");
    setError("");
    setComment("");
    setCurrentAction("addHistory");
  };

  const handleToggleModal = () => {
    setIsModalOpen(false);
  };

  const handleModalAction = async () => {
    if (currentAction === "addHistory") {
      await addHistoryToFollowup();
    }
  };

  const addHistoryToFollowup = async () => {
    if (loading) return;
    if (!comment.trim()) {
      setError("Please enter a message");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        followups_id: selectedFollowupData.followups_id,
        message: comment,
        created_by: userName,
      };

      const response = await addFollowupHistory(payload);

      if (response?.data?.code === 200) {
        setSuccessMessage("History added successfully!");
        setErrorMessage("");
        setError("");
        setTimeout(() => {
          fetchFollowUpData(custId);
          setIsModalOpen(false);
          setSuccessMessage("");
        }, 3000);
      } else {
        setSuccessMessage("");
        setError("");
        setErrorMessage(response?.data?.message || "Failed to add history");
      }
    } catch (error) {
      setSuccessMessage("");
      setError("");
      setErrorMessage(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (custId) {
      fetchFollowUpData(custId);
    }
  }, [custId]);

  return (
    <div className={styles.container}>
      {showForm ? (
        <AddFollowup
          custId={custId}
          onCancel={handleCancelForm}
          onSuccess={handleFormSuccess}
          userName={userName}
          isEditMode={editMode}
          followupData={selectedFollowupData}
        />
      ) : (
        <>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              justifyContent: "flex-end",
              width: "160px",
            }}
          >
            {featureAccess?.writeAccess && (
            <Button
              onClick={handleShowForm}
              title={"Add Follow-up"}
            />
            )}
          </div>
          <div className={styles.dataTableContainer}>
            {dataLoading ? (
              <div>
                <CustomSpinner />
              </div>
            ) : (
              <>
                {errorMessage ? (
                  <div className={styles.noDataContainer}>
                    <div className={styles.noData}>{errorMessage}</div>
                  </div>
                ) : Array.isArray(followUpData) && followUpData.length > 0 ? (
                  <>
                    <DataTable
                      columns={followUpColumns(featureAccess)}
                      list={followUpData}
                      rowClick={false}
                      handleClickRow={handleClickRow}
                      handleEditFollowupClick={handleEditFollowupClick}
                      handleAddHistory={handleAddHistory}
                      pagination={true}
                      title="Follow-ups"
                    />
                  </>
                ) : (
                  <div className={styles.noDataContainer}>
                    <div className={styles.noData}>No Data Found</div>
                  </div>
                )}
              </>
            )}
          </div>

          <Offcanvas
            show={showCanvas}
            onHide={() => setShowCanvas(false)}
            placement="end"
            className={styles.offcanvas}
            backdropClassName={styles.backgroundBlur}
            style={{ color: "white" }}
          >
        <Offcanvas.Header
          closeButton
          style={{
            backgroundColor: "#343434",
            color: "white",
            fontSize: "15px",
            height: "50px",
            marginBottom: "10px",
          }}
        >
          <Offcanvas.Title>Follow-up Details</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {canvasData ? (
            <Accordion
              activeKey={activeKey}
              onSelect={handleToggle}
              className={styles.accordionContainer}
            >
              <Accordion.Item eventKey="0">
                <Accordion.Header className={styles.accordionHeader}>
                  Main Details
                </Accordion.Header>
                <Accordion.Body className={styles.accordionBody}>
                  <table className={styles.details}>
                    <tbody>
                      <tr>
                        <td className={styles.labelColumn}>Subject:</td>
                        <td className={`${styles.textMuted} ${styles.contentColumn}`}>
                          {canvasData.subject || "--"}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.labelColumn}>Description:</td>
                        <td className={`${styles.textMuted} ${styles.contentColumn}`}>
                          {canvasData.description || "--"}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.labelColumn}>Status:</td>
                        <td className={`${styles.textMuted} ${styles.contentColumn}`}>
                          {canvasData.status || "--"}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.labelColumn}>Priority:</td>
                        <td className={`${styles.textMuted} ${styles.contentColumn}`}>
                          {canvasData.priority || "--"}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.labelColumn}>Type:</td>
                        <td className={`${styles.textMuted} ${styles.contentColumn}`}>
                          {canvasData.followupsType || "--"}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.labelColumn}>Platform:</td>
                        <td className={`${styles.textMuted} ${styles.contentColumn}`}>
                          {canvasData.platform || "--"}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.labelColumn}>Created By:</td>
                        <td className={`${styles.textMuted} ${styles.contentColumn}`}>
                          {canvasData.createdBy || "--"}
                        </td>
                      </tr>
                      <tr>
                        <td className={styles.labelColumn}>Created At:</td>
                        <td className={`${styles.textMuted} ${styles.contentColumn}`}>
                          {formatDateAndTime(canvasData.created_at)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1" style={{ marginTop: "20px" }}>
                <Accordion.Header
                  className={styles.accordionHeader}
                >
                  Follow-up History
                </Accordion.Header>
                <Accordion.Body className={styles.accordionBody}>
                  {canvasData.followupHistory &&
                  canvasData.followupHistory.length > 0 ? (
                    <div className={styles.historyContainer}>
                      {canvasData.followupHistory.map((details, index) => (
                        <div key={details._id} className={styles.historyCard}>
                          <div className={styles.historyRow}>
                            <strong style={{ color: "var(--theme-textdiv)" }}>
                              Created At:
                            </strong>
                            <span className={styles.textMuted}>
                              {formatDateAndTime(details.created_at)}
                            </span>
                          </div>
                          <div className={styles.historyRow}>
                            <strong style={{ color: "var(--theme-textdiv)" }}>
                              Created By:
                            </strong>
                            <span className={styles.textMuted}>
                              {details.created_by}
                            </span>
                          </div>
                          <div className={styles.historyRow}>
                            <strong style={{ color: "var(--theme-textdiv)" }}>
                              Message:
                            </strong>
                            <span className={`${styles.textMuted} ${styles.historyMessage}`}>
                              {details.message}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noDataContainer}>
                      <div className={styles.noData}>No history found</div>
                    </div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          ) : (
            <div
              style={{
                height: "100%",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CustomSpinner />
            </div>
          )}
        </Offcanvas.Body>
          </Offcanvas>

          <CommentModal
            setIsComment={setIsComment}
            isOpen={isModalOpen}
            onRequestClose={handleToggleModal}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            errorMessage={errorMessage}
            error={error}
            setError={setError}
            comment={comment}
            setComment={setComment}
            onSave={handleModalAction}
            loading={loading}
            modalTitle="Add Resolution"
          />
        </>
      )}
    </div>
  );
};

export default FollowUp;
